import { removeHash } from '../functionLibrary'
import type {
	IManifestPublicElement,
	ResolvedBuilderWidgetDataItem,
	ResolvedInnerElementsDataItem,
	StyleDataItem,
} from '@wix/thunderbolt-becky-types'
import _ from 'lodash'
import { extractComponentData, REF_DELIMITER } from '@wix/thunderbolt-commons'

const getInnerElementsOverridesMaps = (
	compInnerElementsDataItem: ResolvedBuilderWidgetDataItem
): {
	innerElementsOverrides: Record<string, any>
	dataQueries: Array<string>
	styleQueries: Array<string>
} => {
	const innerElementsOverrides: any = {}
	const dataQueries: Array<string> = []
	const styleQueries: Array<string> = []
	let currInnerElement = innerElementsOverrides

	const recurInnerElements = (innerElementsDataItem: ResolvedBuilderWidgetDataItem, compPath: string) => {
		const overridesMap = innerElementsDataItem?.innerElements || {}

		_.keys(overridesMap).forEach((nickname) => {
			const currNicknamePath = compPath.length ? `${compPath}/${nickname}` : nickname
			currInnerElement[nickname] = {}

			const currInnerElementData: ResolvedInnerElementsDataItem = _.get(
				overridesMap,
				nickname
			)

			if (currInnerElementData.dataQuery) {
				const dataQuery = removeHash(currInnerElementData.dataQuery)!
				currInnerElement[nickname].dataQuery = dataQuery
				dataQueries.push(dataQuery)
			}

			if (currInnerElementData.styleId) {
				const styleId = removeHash(currInnerElementData.styleId)!
				currInnerElement[nickname].styleId = styleId
				styleQueries.push(styleId)
			}

			const slotsQuery = currInnerElementData?.slotsQuery
			if (slotsQuery) {
				currInnerElement[nickname].slotsQuery = slotsQuery
			}

			if (currInnerElementData.innerElementsQuery) {
				const saveParentCompRef = currInnerElement
				currInnerElement = currInnerElement[nickname]
				recurInnerElements(currInnerElementData.innerElementsQuery, currNicknamePath)
				currInnerElement = saveParentCompRef
			}
		})
	}
	if (compInnerElementsDataItem?.innerElements) {
		recurInnerElements(compInnerElementsDataItem, '')
	}

	return { innerElementsOverrides, dataQueries, styleQueries }
}

const isElementRemoved = (style: any) => {
	if (style.type !== 'RefArray') {
		return style?.display === 'none'
	}
	return _.every(style.values, (value) => value?.display === 'none' || value?.to?.display === 'none')
}

const getElementRemovalState = (removedItems: Array<string>, innerElement: boolean = false) => {
	const removedItemsMap = Object.fromEntries(removedItems.map((item) => [item, 'REMOVED']))
	return removedItems.length > 0
		? innerElement
			? { wix: { elementsRemovalState: removedItemsMap } }
			: { elementsRemovalState: removedItemsMap }
		: {}
}

/**
 * Resolves inner elements properties by replacing string references with actual data items.
 * This function modifies the data to be the actual props expected to pass to the component, using the 'elementProps' notation and excluding internal properties.
 *
 * @param {Record<string, any>} innerElementsDataOverrides - The overrides for inner elements.
 * @param {Record<string, any>} dataItems - The data items to use for resolving references.
 * @param {Record<string, StyleDataItem>} styleItems - The style items to use for resolving references.
 * @returns {Record<string, any>} - The resolved inner elements props.
 */
const resolveInnerElementsProps = (
	innerElementsDataOverrides: Record<string, any>,
	dataItems: Record<string, any>,
	styleItems: Record<string, StyleDataItem>,
	slotsData: Record<string, { slots: Record<string, string> }>,
	compId: string,
	reducedManifest: IManifestPublicElement['reducedComponentModel']
) => {
	const resolveElementPropsItemOverrides = (
		key: string,
		val: any,
		dataTarget: Record<string, any>,
		path: string = ''
	) => {
		const { dataQuery, styleId, slotsQuery, ...elementProps } = val

		const removedItems = []

		let data: Record<string, any> = {}

		if (dataItems[dataQuery]?.data) {
			data = extractComponentData(dataItems[dataQuery])
		}
		if (styleId && isElementRemoved(styleItems[styleId])) {
			removedItems.push(key)
		}

		const slotData = slotsData?.[slotsQuery]?.slots
		const currentPath = path.length > 0 ? `${path}/${key}` : key
		if (slotData) {
			data = { ...data, ...resolveElementSlots(slotData, compId, reducedManifest, currentPath) }
		}

		if (Object.keys(elementProps).length > 0) {
			data.elementProps = {}

			Object.entries(elementProps).forEach(([itemKey, itemVal]) => {
				const InnerRemovedItems = resolveElementPropsItemOverrides(itemKey, itemVal, data.elementProps, currentPath)
				data = {
					...data,
					...getElementRemovalState(InnerRemovedItems, true),
				}
			})
			if (Object.keys(data.elementProps).length === 0) {
				delete data.elementProps
			}
		}
		if (Object.keys(data).length > 0) {
			dataTarget[key] = data
		}

		return removedItems
	}

	const dataOverrides = {
		elementProps: {},
	}
	const removedItems: Array<string> = []

	Object.entries(innerElementsDataOverrides).forEach(([innerElementId, innerElementData]: [string, any]) => {
		removedItems.push(
			...resolveElementPropsItemOverrides(innerElementId, innerElementData, dataOverrides.elementProps)
		)
	})

	return {
		...dataOverrides,
		...getElementRemovalState(removedItems),
	}
}

function getRefPrefix(compId: string) {
	return compId.substring(0, compId.lastIndexOf(REF_DELIMITER) + REF_DELIMITER.length)
}

function resolveElemenetCompId(compId: string, slotCompId: string) {
	const isRefComponent = compId.includes(REF_DELIMITER)
	const prefix = isRefComponent ? getRefPrefix(compId) : ''
	return `${prefix}${slotCompId}`
}

function resolveElementSlots(
	slotData: Record<string, string>,
	compId: string,
	reducedManifest: IManifestPublicElement['reducedComponentModel'],
	currentPath: string
) {
	return _.mapValues(slotData, (slotCompId, containerName) => {
		const ROOT_PREFIX = 'root/'
		const currentPathWithRootPrefix = `${ROOT_PREFIX}${currentPath}`
		const containerData = reducedManifest?.elementData?.[currentPathWithRootPrefix]?.containersData?.[containerName]

		return { compId: resolveElemenetCompId(compId, slotCompId), ...containerData }
	})
}

export { getInnerElementsOverridesMaps, resolveInnerElementsProps }
